import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Familia() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCTA = () => {
    const widget = document.querySelector('elevenlabs-convai') as HTMLElement & { open?: () => void };
    if (widget && typeof widget.open === 'function') {
      widget.open();
    } else {
      const btn = widget?.shadowRoot?.querySelector('button') as HTMLElement | null;
      if (btn) btn.click();
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#0D0D0D]/95 backdrop-blur-md shadow-lg shadow-black/30' : 'bg-transparent'}`}>
        <div className="px-5 md:px-8 py-4 md:py-5 flex items-center justify-between">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/5a93e3026f3c378ff1b81daef74f8459.png"
              alt="AgenteFinanciero Logo"
              className="h-8 md:h-10 w-auto object-contain brightness-0 invert"
            />
          </Link>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/familia" className="text-sm font-semibold text-[#E8A045] whitespace-nowrap cursor-pointer tracking-wide">
              Para tu Familia
            </Link>
            <Link to="/agente-financiero" className="text-sm font-medium text-white/70 hover:text-[#E8A045] transition-colors whitespace-nowrap cursor-pointer tracking-wide">
              Carrera como Agente
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>
        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-[#0D0D0D]/98 border-t border-white/10 px-5 py-4 flex flex-col gap-4">
            <Link to="/familia" onClick={() => setMenuOpen(false)} className="text-sm font-semibold text-[#E8A045] whitespace-nowrap cursor-pointer tracking-wide py-2">
              Para tu Familia
            </Link>
            <Link to="/agente-financiero" onClick={() => setMenuOpen(false)} className="text-sm font-medium text-white/70 hover:text-[#E8A045] transition-colors whitespace-nowrap cursor-pointer tracking-wide py-2">
              Carrera como Agente
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/ea6d996c885b67d82badca5f438a75ef.png"
            alt="Familia protegida"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/85 to-[#0D0D0D]/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-[#0D0D0D]/20"></div>
        </div>

        <div className="relative z-10 w-full px-5 md:px-8 pt-28 md:pt-32 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-[#E8A045]/10 border border-[#E8A045]/30 rounded-full px-4 py-2 mb-6 md:mb-8">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-heart-fill text-[#E8A045] text-sm"></i>
                </div>
                <span className="text-[#E8A045] text-xs md:text-sm font-medium tracking-wider uppercase">Protección Familiar</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] mb-6 md:mb-8 tracking-tight">
                Quiero que mi<br />
                familia esté bien,<br />
                <span className="text-[#E8A045]">pase lo que pase.</span>
              </h1>

              <p className="text-base md:text-lg text-white/50 mb-8 md:mb-12 leading-relaxed max-w-lg">
                Proteger a tu familia no tiene que ser complicado. Te ayudamos a entender tus opciones en lenguaje sencillo, sin presión.
              </p>

              <button
                onClick={handleCTA}
                className="group bg-[#E8A045] hover:bg-[#d4913a] transition-all duration-300 rounded-full px-6 md:px-8 py-4 flex items-center gap-3 shadow-lg shadow-[#E8A045]/20 hover:shadow-[#E8A045]/40 hover:-translate-y-0.5 transform cursor-pointer w-full sm:w-auto"
              >
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-voice-fill text-[#0D0D0D] text-base"></i>
                </div>
                <span className="font-bold text-[#0D0D0D] text-sm md:text-base whitespace-nowrap">Hablar con un AgenteFinanciero virtual</span>
                <div className="w-6 h-6 flex items-center justify-center group-hover:translate-x-1 transition-transform flex-shrink-0">
                  <i className="ri-arrow-right-line text-[#0D0D0D]"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Points Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight">
              Lo que realmente <span className="text-[#E8A045]">importa</span>
            </h2>
            <p className="text-white/40 text-base md:text-lg max-w-xl">Tres preocupaciones reales que podemos resolver juntos.</p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                icon: 'ri-home-heart-fill',
                title: 'Casa, comida y escuela',
                desc: 'Si algo te pasa, tu familia no debería preocuparse por cubrir los gastos básicos. Un seguro de vida garantiza que mantengan su nivel de vida.',
                num: '01',
              },
              {
                icon: 'ri-shield-cross-fill',
                title: 'Sin deudas ni problemas',
                desc: 'Las deudas pueden convertirse en una carga para tus seres queridos. Planifica para que tu familia herede tranquilidad, no preocupaciones.',
                num: '02',
              },
              {
                icon: 'ri-time-fill',
                title: 'Retiro con dignidad',
                desc: 'Complementar tu retiro con opciones adicionales te da control sobre tu futuro. No dejes tu bienestar al azar ni al gobierno.',
                num: '03',
              },
            ].map((item, i) => (
              <div key={i} className="group bg-[#161616] border border-white/5 hover:border-[#E8A045]/30 rounded-2xl p-6 md:p-8 transition-all duration-400 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#E8A045]/5">
                <div className="flex items-start justify-between mb-6 md:mb-8">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E8A045]/10 rounded-xl border border-[#E8A045]/20">
                    <i className={`${item.icon} text-[#E8A045] text-xl`}></i>
                  </div>
                  <span className="text-white/10 font-black text-3xl md:text-4xl">{item.num}</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Review Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#111111] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#E8A045]/10 border border-[#E8A045]/30 rounded-full px-4 py-2 mb-6 md:mb-8">
                <span className="text-[#E8A045] text-xs md:text-sm font-medium tracking-wider uppercase">Sin compromiso</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-5 md:mb-6 tracking-tight leading-tight">
                Qué podemos<br />
                <span className="text-[#E8A045]">revisar contigo</span>
              </h2>
              <p className="text-white/50 text-base md:text-lg leading-relaxed mb-8 md:mb-10">
                Sin presión, sin compromisos. Solo una conversación honesta sobre lo que necesitas para proteger a los tuyos.
              </p>
              <div className="space-y-3 md:space-y-4">
                {[
                  { icon: 'ri-shield-user-fill', label: 'Seguro de vida para proteger ingresos' },
                  { icon: 'ri-wallet-3-fill', label: 'Opciones para complementar tu retiro' },
                  { icon: 'ri-footprint-fill', label: 'Primeros pasos aunque nunca hayas hablado de estos temas' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 bg-[#161616] rounded-xl px-5 md:px-6 py-4 border border-white/5">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#E8A045]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#E8A045] text-lg`}></i>
                    </div>
                    <span className="text-white/80 font-medium text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative mt-8 md:mt-0">
              <div className="rounded-2xl overflow-hidden h-72 md:h-[480px] w-full">
                <img
                  src="https://public.readdy.ai/ai/img_res/84591bd2afe000578b5d2bf4bc45b52c.jpg"
                  alt="Asesoría financiera familiar"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/60 to-transparent rounded-2xl"></div>
              </div>
              <div className="absolute -bottom-4 right-2 md:-bottom-5 md:-right-5 bg-[#161616] border border-[#E8A045]/30 rounded-2xl px-4 md:px-6 py-4 md:py-5 shadow-2xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-check-double-fill text-[#E8A045] text-xl"></i>
                  </div>
                  <span className="text-white font-bold text-xs md:text-sm">Sin jerga complicada</span>
                </div>
                <p className="text-white/40 text-xs">Todo explicado en lenguaje sencillo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight">
              Cómo funciona <span className="text-[#E8A045]">el proceso</span>
            </h2>
            <p className="text-white/40 text-base md:text-lg max-w-xl mx-auto">Tres pasos simples para empezar a proteger a tu familia hoy.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 md:gap-8 relative">
            <div className="hidden md:block absolute top-8 left-1/3 right-1/3 h-px bg-gradient-to-r from-[#E8A045]/30 via-[#E8A045]/60 to-[#E8A045]/30"></div>
            {[
              { step: '1', icon: 'ri-chat-voice-fill', title: 'Habla con el agente virtual', desc: 'Responde unas preguntas sencillas sobre tu situación familiar y tus necesidades.' },
              { step: '2', icon: 'ri-search-eye-fill', title: 'Revisamos tus opciones', desc: 'El agente identifica qué tipo de cobertura tiene más sentido para ti y tu familia.' },
              { step: '3', icon: 'ri-calendar-check-fill', title: 'Agenda una llamada humana', desc: 'Si tiene sentido, agendamos una llamada con un asesor real para profundizar.' },
            ].map((item, i) => (
              <div key={i} className="text-center relative">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-[#E8A045] rounded-full mx-auto mb-5 md:mb-6 shadow-lg shadow-[#E8A045]/30">
                  <i className={`${item.icon} text-[#0D0D0D] text-xl md:text-2xl`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed max-w-xs mx-auto">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#111111] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-[#161616] border border-[#E8A045]/20 p-8 md:p-12 lg:p-16 text-center shadow-2xl">
            <div className="absolute inset-0 opacity-5">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20golden%20geometric%20pattern%20financial%20protection%20shield%20dark%20background%20minimal%20texture&width=1200&height=600&seq=cta-bg-familia-001&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-[#E8A045]/10 rounded-full mx-auto mb-6 md:mb-8 border border-[#E8A045]/30">
                <i className="ri-chat-voice-fill text-[#E8A045] text-2xl md:text-3xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-5 md:mb-6 tracking-tight leading-tight">
                ¿Listo para dar<br />
                <span className="text-[#E8A045]">el primer paso?</span>
              </h2>
              <p className="text-white/50 text-base md:text-lg mb-4 leading-relaxed max-w-2xl mx-auto">
                Al dar clic, podrás hablar con nuestro AgenteFinanciero virtual que te hará preguntas sencillas y te ayudará a agendar una cita si realmente es buen momento para ti.
              </p>
              <p className="text-white/30 text-sm mb-8 md:mb-10">
                Te haremos unas preguntas sencillas y, si tiene sentido, agendaremos una llamada con un asesor humano.
              </p>
              <button
                onClick={handleCTA}
                className="group inline-flex items-center gap-3 bg-[#E8A045] hover:bg-[#d4913a] transition-all duration-300 rounded-full px-7 md:px-10 py-4 md:py-5 shadow-xl shadow-[#E8A045]/20 hover:shadow-[#E8A045]/40 hover:-translate-y-1 transform cursor-pointer w-full sm:w-auto justify-center"
              >
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-voice-fill text-[#0D0D0D] text-lg"></i>
                </div>
                <span className="font-black text-[#0D0D0D] text-sm md:text-base lg:text-lg whitespace-nowrap">Hablar con un AgenteFinanciero virtual</span>
                <div className="w-6 h-6 flex items-center justify-center group-hover:translate-x-1 transition-transform flex-shrink-0">
                  <i className="ri-arrow-right-line text-[#0D0D0D] text-lg"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-8 md:mb-12 tracking-tight">
            Familias que ya <span className="text-[#E8A045]">dieron el paso</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
            {[
              {
                initials: 'MC',
                name: 'María Contreras',
                role: 'Madre de 2 hijos, CDMX',
                quote: '"Siempre pensé que los seguros eran complicados, pero me explicaron todo tan claro que finalmente entendí por qué era importante. Ahora duermo tranquila sabiendo que mis hijos están protegidos."',
              },
              {
                initials: 'JR',
                name: 'Jorge Ramírez',
                role: 'Profesional independiente, Monterrey',
                quote: '"Como freelancer, no tenía ningún plan de retiro. El asesor me ayudó a encontrar opciones que se ajustan a mis ingresos variables. Ahora estoy construyendo mi futuro poco a poco."',
              },
            ].map((t, i) => (
              <div key={i} className="bg-[#161616] border border-white/5 rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-2 mb-5 md:mb-6">
                  {[...Array(5)].map((_, s) => (
                    <div key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-[#E8A045] text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6 md:mb-8 italic">{t.quote}</p>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#E8A045]/10 border border-[#E8A045]/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#E8A045] font-bold text-sm">{t.initials}</span>
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">{t.name}</div>
                    <div className="text-white/30 text-xs">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111111] border-t border-white/5 py-10 md:py-12 px-5 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6 md:flex-row md:justify-between">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/5a93e3026f3c378ff1b81daef74f8459.png"
              alt="AgenteFinanciero Logo"
              className="h-8 md:h-10 w-auto object-contain brightness-0 invert opacity-60"
            />
          </Link>
          <p className="text-white/30 text-xs md:text-sm text-center">© 2025 AgenteFinanciero. Protegiendo familias y construyendo carreras.</p>
          <div className="flex items-center gap-5 md:gap-6 text-sm">
            <a href="#" className="text-white/30 hover:text-[#E8A045] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm">Privacidad</a>
            <a href="#" className="text-white/30 hover:text-[#E8A045] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm">Términos</a>
            <a href="#" className="text-white/30 hover:text-[#E8A045] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
